import { Button } from "@fluentui/react-components";
import { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import { resetChat } from "chatBotEventsSlice";
import { show, hide } from "components/Home/homeVisibilitySlice";
import { getThinking, setThinking } from "components/thinking/thinkingState";
import { setTextToSearch } from "shared/components/Cards/Card/cardTextSlice";
import { openOrCloseModal } from "shared/components/Modal/modalActionsSlice";
import { toggleSideNav } from "shared/components/SideNav/SideNavToggleButton/toggleSideNav";
import { userSendPrompt } from "shared/components/SideNav/userSendPromptSlicer";
import api from "shared/functions/api";

import './PromptButton.css';
import RefreshBlueIcon from './RefreshBlueIcon';
import { ModalType } from "constants/enums";

// const { useSendBoxValue } = hooks;
const BulbIcon = ({ active }: { active: boolean }) => {
  const color = active ? '#0074D4' : '#B0B0B0';
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Rays — only when active */}
      {active && <>
        <line x1="8" y1="0.5" x2="8" y2="2" stroke={color} strokeWidth="1.1" strokeLinecap="round"/>
        <line x1="12.5" y1="2" x2="11.5" y2="3" stroke={color} strokeWidth="1.1" strokeLinecap="round"/>
        <line x1="14.5" y1="6.5" x2="13" y2="6.5" stroke={color} strokeWidth="1.1" strokeLinecap="round"/>
        <line x1="3.5" y1="2" x2="4.5" y2="3" stroke={color} strokeWidth="1.1" strokeLinecap="round"/>
        <line x1="1.5" y1="6.5" x2="3" y2="6.5" stroke={color} strokeWidth="1.1" strokeLinecap="round"/>
      </>}
      {/* Bulb body */}
      <path d="M8 3C5.79 3 4 4.79 4 7c0 1.48.8 2.77 2 3.46V12h4v-1.54c1.2-.69 2-1.98 2-3.46 0-2.21-1.79-4-4-4Z" stroke={color} strokeWidth="1.1" fill="none" strokeLinejoin="round"/>
      {/* Base lines */}
      <line x1="5.5" y1="12.5" x2="10.5" y2="12.5" stroke={color} strokeWidth="1.1" strokeLinecap="round"/>
      <line x1="6" y1="13.8" x2="10" y2="13.8" stroke={color} strokeWidth="1.1" strokeLinecap="round"/>
    </svg>
  );
};

const NewChatIcon = () => {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className='icon'>
      {/* Document frame */}
      <rect x="1" y="1" width="9" height="12" rx="1.5" stroke="#0074D4" strokeWidth="1.2" fill="none"/>
      {/* Pencil overlapping bottom-right */}
      <path d="M8 8.5L10.5 6L12 7.5L9.5 10H8V8.5Z" stroke="#0074D4" strokeWidth="1" strokeLinejoin="round" fill="white"/>
      <line x1="9.3" y1="7.2" x2="10.8" y2="8.7" stroke="#0074D4" strokeWidth="0.9" strokeLinecap="round"/>
    </svg>
  )
}

const HistoryIcon = () => {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className='icon'>
      {/* Clock face with open arc (history / restore) */}
      <path d="M2.6 4.2A5.2 5.2 0 1 1 1.8 7" stroke="#0074D4" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
      {/* Arrow head pointing back */}
      <path d="M2.6 1.6V4.3H5.3" stroke="#0074D4" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      {/* Clock hands */}
      <path d="M7 4.4V7L8.9 8.4" stroke="#0074D4" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  )
}

const StarsIcon = () => {
  return (
    <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg" className='icon'>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M8.88344 4.61774C9.15973 4.61774 9.40694 4.78929 9.50344 5.04859L10.2292 7.00555C10.4083 7.48984 10.7891 7.86989 11.2736 8.04869L13.2347 8.77315C13.4938 8.86882 13.6663 9.11559 13.6663 9.39204C13.6663 9.66784 13.4938 9.9146 13.2347 10.0103L11.1176 10.7928C10.7322 10.9346 10.4288 11.2375 10.2867 11.6222L9.50344 13.7355C9.4076 13.9941 9.15973 14.1663 8.88344 14.1663C8.60714 14.1663 8.35993 13.9948 8.26343 13.7355L7.49933 11.6756C7.34466 11.2566 7.01482 10.9281 6.59576 10.773L4.53149 10.0103C4.27239 9.9146 4.10053 9.66784 4.10053 9.39204C4.10053 9.11559 4.27239 8.86882 4.53149 8.77315L6.46687 8.05859C6.96856 7.87319 7.36184 7.48061 7.54692 6.97982L8.26343 5.04859C8.35993 4.78929 8.60714 4.61774 8.88344 4.61774ZM8.88344 7.17842L8.78693 7.43772C8.46768 8.30008 7.7895 8.97703 6.92559 9.29571L6.66582 9.39204L7.05448 9.53522C7.83643 9.82355 8.45049 10.4372 8.73934 11.2177L8.88278 11.605L9.0467 11.1649C9.32365 10.418 9.91127 9.83147 10.6588 9.55501L11.1004 9.39204L10.8148 9.28647C9.96812 8.97373 9.3025 8.30931 8.98919 7.46411L8.88344 7.17842ZM3.79394 0.833008C4.07023 0.833008 4.3181 1.00522 4.41394 1.26386L4.90704 2.59335C5.00685 2.86387 5.22035 3.07632 5.49135 3.17661L6.82324 3.66882C7.08235 3.76449 7.25487 4.01126 7.25487 4.28771C7.25487 4.56351 7.08301 4.81027 6.82324 4.9066L5.3856 5.43774C5.18135 5.51296 5.02139 5.67263 4.94604 5.87584L4.41394 7.31157C4.31955 7.56629 4.07771 7.73718 3.80648 7.7423L3.79394 7.74241C3.51765 7.74241 3.27044 7.57021 3.17393 7.31157L2.65506 5.91213C2.57111 5.68582 2.39331 5.50768 2.16659 5.42388L0.764633 4.9066C0.505526 4.81027 0.333008 4.56351 0.333008 4.28771C0.333008 4.01126 0.505526 3.76449 0.764633 3.66882L2.07868 3.18321C2.36158 3.07896 2.58301 2.85727 2.68745 2.57553L3.17393 1.26386C3.27044 1.00522 3.51765 0.833008 3.79394 0.833008ZM3.79262 3.33166C3.57581 3.73414 3.24664 4.06404 2.84476 4.28177C3.25259 4.48631 3.58374 4.81423 3.79195 5.21935C3.99422 4.81093 4.32603 4.48103 4.73651 4.28111C4.33463 4.0647 4.00678 3.7348 3.79262 3.33166Z" fill="#0074D4"/>
    </svg>
  )
}


function PromptButton(props: { cardText: string; reconnectFailed?: boolean }) {

  let dispatch = useDispatch()
  // Visible solo mientras se muestra la pantalla de bienvenida (F5 /
  // New Chat); en cuanto se manda un mensaje o se carga una
  // conversación, homeVisibility pasa a false y el botón desaparece.
  const homeVisible = useSelector((state: any) => state.homeVisibility.value);
  const [loadingLast, setLoadingLast] = useState(false);
  const [noLastConversation, setNoLastConversation] = useState(false);
  const [thinkingMode, setThinkingMode] = useState<boolean | null>(() => getThinking());
  // const [sendBoxValue, setSendBoxValue] = useSendBoxValue();

  if(typeof props.cardText == "string" && props.cardText !== ""){
    // setSendBoxValue(props.cardText)
    dispatch(setTextToSearch(''));

  }

  const handleClick = useCallback(() => {
    dispatch(openOrCloseModal({ isOpen: true, modalType: ModalType.Prompts }))
  }, [dispatch]);

  const handleToggleThink = useCallback(() => {
    setThinkingMode((prev) => {
      const next = prev == null ? true : !prev;
      setThinking(next);
      return next;
    });
  }, []);

  const handleNewChat = useCallback(async () => {
    dispatch(userSendPrompt('New chat'));
    dispatch(toggleSideNav(false));
    const jwt = localStorage.getItem('jwt') || '';
    let newToken = await api.fetchToken(jwt);
    localStorage.setItem('newToken', JSON.stringify(newToken));
    // Un chat nuevo nunca retoma una sesión anterior.
    localStorage.removeItem('resumeSessionId');
    dispatch(show());
    dispatch(resetChat(true));
  }, [dispatch]);

  // Carga la conversación más reciente del usuario: mismo flujo que
  // abrirla desde el panel lateral (historial visual + evento de
  // resume para que el bot continúe donde se quedó).
  const handleLoadLastConversation = useCallback(async () => {
    if (loadingLast) return;
    setLoadingLast(true);
    try {
      const jwt = localStorage.getItem('jwt') || '';
      const conversations = await api.fetchConversationsIds(jwt);
      const dates = Object.keys(conversations || {});
      const lastConversation = dates.length ? (conversations[dates[0]] || [])[0] : undefined;
      if (!lastConversation || !lastConversation.id) {
        // No hay conversaciones recientes: se oculta el botón.
        setNoLastConversation(true);
        return;
      }
      const newToken = await api.fetchConversationHistory(lastConversation.id, jwt);
      localStorage.setItem('newToken', JSON.stringify(newToken));
      localStorage.setItem('resumeSessionId', lastConversation.id);
      dispatch(hide());
      dispatch(resetChat(true));
    } catch (error) {
      console.error('Could not load last conversation:', error);
    } finally {
      setLoadingLast(false);
    }
  }, [dispatch, loadingLast]);

  const thinkLabel = thinkingMode == null ? 'Auto' : thinkingMode ? 'On' : 'Off';
  const bulbActive = thinkingMode === true;

  return (
    <div className="prompts-button-container">
      {homeVisible && !noLastConversation && (
        <Button
          onClick={handleLoadLastConversation}
          type="button"
          icon={<HistoryIcon />}
          className='prompts-button load-last-button'
          disabled={loadingLast}
        >
          {loadingLast ? 'Loading...' : 'Load last conversation'}
        </Button>
      )}
      <button
        type="button"
        className={`bulb-icon-button${bulbActive ? ' bulb-icon-button--active' : ''}`}
        onClick={handleToggleThink}
        aria-pressed={thinkingMode === true}
        title="Think"
      >
        <BulbIcon active={bulbActive} />
        <span className="bulb-icon-label">Think</span>
      </button>
      {props.reconnectFailed && (
        <>
          <span className="reconnect-message reconnect-message--failed" style={{marginRight: 8}}>
            Inactive session - Refresh required.
          </span>
          <Button onClick={() => window.location.reload()} type="button" icon={<RefreshBlueIcon />} className='prompts-button new-chat-blink'>
            Refresh
          </Button>
        </>
      )}
      <Button onClick={handleNewChat} type="button" icon={<NewChatIcon />} className='prompts-button'>
        New Chat
      </Button>
      <Button onClick={handleClick} type="button" icon={<StarsIcon />} className='prompts-button'>
        Prompts to try
      </Button>
    </div>
  );
}

export default memo(PromptButton);
